// Service API pour SnowCash
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('snowcash_token');
    this.demoMode = DEMO_MODE;
    
    // Rafraîchir le token depuis le localStorage à chaque utilisation
    this.refreshToken();
  }

  // Rafraîchir le token depuis le localStorage
  refreshToken() {
    this.token = localStorage.getItem('snowcash_token');
  }

  // Headers par défaut
  getHeaders() {
    // Toujours rafraîchir le token avant de créer les headers
    this.refreshToken();
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    return headers;
  }

  // Gestion des erreurs
  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur API');
    }
    return response.json();
  }

  // Vérifier si le backend est disponible
  async checkBackendAvailability() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return response.ok;
    } catch (error) {
      console.warn('Backend non disponible, basculement en mode démo');
      return false;
    }
  }

  // Mode démo pour les fonctions critiques
  getDemoResponse(type, data = {}) {
    const responses = {
      auth: {
        success: true,
        token: 'demo-token-' + Date.now(),
        user: {
          id: 1,
          walletAddress: data.walletAddress || '0x1234...5678',
          isNewUser: false,
          hasHyperliquidKeys: false,
          botActive: false
        }
      },
      setKeys: {
        success: true,
        message: 'Clés configurées en mode démo'
      },
      toggleBot: {
        success: true,
        botActive: data.action === 'start',
        message: `Bot ${data.action === 'start' ? 'démarré' : 'arrêté'} en mode démo`
      },
      stats: {
        success: true,
        stats: {
          total_trades: 42,
          winning_trades: 28,
          total_pnl: 156.78,
          avg_pnl: 3.73,
          win_rate: 66.7
        }
      },
      portfolio: {
        success: true,
        portfolio: {
          balance: 1250.75 + Math.random() * 100,
          pnl: 45.32 + (Math.random() - 0.5) * 20,
          totalTrades: 23,
          winRate: 65.2,
          positions: []
        }
      }
    };
    
    return responses[type] || { success: false, error: 'Type de réponse démo non trouvé' };
  }

  // 🔐 Authentification MetaMask
  async authenticateWithMetaMask(walletAddress, signature, message) {
    try {
      // Vérifier si le backend est disponible
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable) {
        console.warn('🔄 Mode démo activé - Backend non disponible');
        // Utiliser le mode démo
        const result = this.getDemoResponse('auth', { walletAddress });
        
        this.token = result.token;
        localStorage.setItem('snowcash_token', result.token);
        localStorage.setItem('snowcash_user', JSON.stringify(result.user));
        localStorage.setItem('snowcash_demo_mode', 'true');
        
        return result;
      }

      const response = await fetch(`${API_BASE_URL}/auth/metamask`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          walletAddress,
          signature,
          message
        })
      });

      const result = await this.handleResponse(response);
      
      if (result.success) {
        this.token = result.token;
        localStorage.setItem('snowcash_token', result.token);
        localStorage.setItem('snowcash_user', JSON.stringify(result.user));
        localStorage.removeItem('snowcash_demo_mode');
      }
      
      return result;
    } catch (error) {
      console.error('Erreur authentification:', error);
      
      // Fallback en mode démo
      console.warn('🔄 Basculement en mode démo suite à l\'erreur');
      const result = this.getDemoResponse('auth', { walletAddress });
      
      this.token = result.token;
      localStorage.setItem('snowcash_token', result.token);
      localStorage.setItem('snowcash_user', JSON.stringify(result.user));
      localStorage.setItem('snowcash_demo_mode', 'true');
      
      return result;
    }
  }

  // 🔑 Configuration des clés Hyperliquid
  async setHyperliquidKeys(apiKey, secretKey) {
    try {
      // Vérifier si le backend est disponible ou si on est en mode démo
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        console.warn('🔄 Mode démo - Configuration clés simulée');
        return this.getDemoResponse('setKeys');
      }

      const response = await fetch(`${API_BASE_URL}/user/hyperliquid-keys`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          apiKey,
          secretKey
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur configuration clés:', error);
      
      // Fallback en mode démo
      console.warn('🔄 Basculement en mode démo pour configuration clés');
      return this.getDemoResponse('setKeys');
    }
  }

  // 🤖 Contrôle du bot
  async toggleBot(action) {
    try {
      // Vérifier si le backend est disponible ou si on est en mode démo
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        console.warn('🔄 Mode démo - Contrôle bot simulé');
        return this.getDemoResponse('toggleBot', { action });
      }

      const response = await fetch(`${API_BASE_URL}/bot/toggle`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ action })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur contrôle bot:', error);
      
      // Fallback en mode démo
      console.warn('🔄 Basculement en mode démo pour contrôle bot');
      return this.getDemoResponse('toggleBot', { action });
    }
  }

  // 📊 Récupérer les statistiques
  async getUserStats() {
    try {
      // Vérifier si le backend est disponible ou si on est en mode démo
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        return this.getDemoResponse('stats');
      }

      const response = await fetch(`${API_BASE_URL}/user/stats`, {
        headers: this.getHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération stats:', error);
      
      // Fallback en mode démo
      return this.getDemoResponse('stats');
    }
  }

  // 📈 Données du portefeuille
  async getPortfolioData() {
    try {
      // Vérifier si le backend est disponible ou si on est en mode démo
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        return this.getDemoResponse('portfolio');
      }

      const response = await fetch(`${API_BASE_URL}/user/portfolio`, {
        headers: this.getHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur données portefeuille:', error);
      
      // Fallback en mode démo
      return this.getDemoResponse('portfolio');
    }
  }

  // 🏥 Vérifier l'état du serveur
  async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur santé serveur:', error);
      throw error;
    }
  }

  // 🚪 Déconnexion
  logout() {
    this.token = null;
    localStorage.removeItem('snowcash_token');
    localStorage.removeItem('snowcash_user');
  }

  // 👤 Récupérer l'utilisateur stocké
  getCurrentUser() {
    const user = localStorage.getItem('snowcash_user');
    return user ? JSON.parse(user) : null;
  }

  // 🔍 Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    return !!this.token;
  }

  // 🎭 Vérifier si on est en mode démo
  isDemoMode() {
    return localStorage.getItem('snowcash_demo_mode') === 'true';
  }

  // 📊 Récupérer les données du portfolio
  async getPortfolioData() {
    try {
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        // Simulation des données portfolio avec variation temps réel
        const baseBalance = 1250.75;
        const variation = (Math.random() - 0.5) * 50; // Variation de ±25
        const currentBalance = baseBalance + variation;
        const pnl = variation + (Math.random() - 0.3) * 30; // Légèrement profitable
        
        return {
          success: true,
          data: {
            balance: currentBalance,
            usdcBalance: 850.25 + (Math.random() - 0.5) * 20,
            totalValue: currentBalance + 200 + (Math.random() - 0.5) * 40,
            pnl: pnl,
            pnlPercent: (pnl / baseBalance) * 100,
            totalTrades: Math.floor(Math.random() * 50) + 10, // Simulation 10-60 trades
            winRate: 65 + (Math.random() - 0.5) * 20, // Simulation 55-75% win rate
            positions: [
              { 
                symbol: 'SOL', 
                size: 5.2, 
                value: 520 + (Math.random() - 0.5) * 50, 
                pnl: 45.2 + (Math.random() - 0.5) * 20 
              },
              { 
                symbol: 'ETH', 
                size: 0.8, 
                value: 2400 + (Math.random() - 0.5) * 100, 
                pnl: -12.5 + (Math.random() - 0.5) * 15 
              }
            ],
            lastUpdate: new Date().toISOString()
          }
        };
      }

      const response = await fetch(`${API_BASE_URL}/user/portfolio`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur récupération portfolio:', error);
      
      // Fallback en mode démo avec données basiques
      return {
        success: true,
        data: {
          balance: 1000,
          usdcBalance: 500,
          totalValue: 1000,
          pnl: 0,
          pnlPercent: 0,
          totalTrades: 0,
          winRate: 0,
          positions: [],
          lastUpdate: new Date().toISOString()
        }
      };
    }
  }

  // 💸 Initier un dépôt USDC
  async initiateDeposit(amount, autoDeposit = false) {
    try {
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        // Simulation du dépôt en mode démo
        return {
          success: true,
          deposit: {
            success: true,
            depositData: {
              amount: amount.toString(),
              destination: 'Demo Hyperliquid Account',
              signature: 'demo-signature',
              instructions: {
                step1: 'Approuver le contrat USDC (Démo)',
                step2: 'Confirmer le transfert (Démo)',
                step3: 'Attendre la confirmation blockchain (Démo)',
                contractAddress: '0x07865c6E87B9F70255377e024ace6630C1Eaa37F',
                hyperliquidContract: '0x1234567890123456789012345678901234567890'
              }
            }
          }
        };
      }

      const response = await fetch(`${API_BASE_URL}/user/deposit`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ 
          amount,
          autoDeposit 
        })
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur dépôt:', error);
      
      // Fallback en mode démo
      return {
        success: true,
        deposit: {
          success: true,
          depositData: {
            amount: amount.toString(),
            destination: 'Demo Hyperliquid Account (Erreur)',
            message: 'Dépôt simulé suite à une erreur'
          }
        }
      };
    }
  }

  // 📊 Récupérer l'historique des transactions
  async getTransactionHistory(limit = 50) {
    try {
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        // Données simulées
        return {
          success: true,
          transactions: [
            {
              id: '1',
              timestamp: new Date(Date.now() - 3600000),
              type: 'BUY',
              asset: 'SOL',
              amount: 0.5,
              price: 225.30,
              fee: 0.15,
              pnl: 0
            },
            {
              id: '2',
              timestamp: new Date(Date.now() - 7200000),
              type: 'SELL',
              asset: 'SOL',
              amount: 0.3,
              price: 228.45,
              fee: 0.12,
              pnl: 1.58
            }
          ],
          isDemo: true
        };
      }

      const response = await fetch(`${API_BASE_URL}/user/transactions?limit=${limit}`, {
        headers: this.getHeaders()
      });

      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur historique transactions:', error);
      
      // Fallback en mode démo
      return {
        success: true,
        transactions: [],
        isDemo: true,
        error: 'Erreur récupération historique'
      };
    }
  }

  // 📊 Récupérer les données de marché
  async getMarketData(symbol = 'SOL') {
    try {
      const backendAvailable = await this.checkBackendAvailability();
      
      if (!backendAvailable || this.isDemoMode()) {
        // Données simulées
        return {
          success: true,
          price: 225.30 + (Math.random() - 0.5) * 10,
          symbol,
          timestamp: Date.now(),
          isDemo: true
        };
      }

      const response = await fetch(`${API_BASE_URL}/market/${symbol}`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur données marché:', error);
      
      // Fallback en mode démo
      return {
        success: true,
        price: 225.30,
        symbol,
        timestamp: Date.now(),
        isDemo: true,
        error: 'Erreur récupération données marché'
      };
    }
  }
}

export default new ApiService();
